import { Fragment, useMemo, useState } from 'react';
import { apiLoadErrorText, useGetMtoReceiptSummaryQuery } from '@shared/api';
import { Select } from '@shared/ui/Input';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { Notice, SegmentButton, Segmented, TextButton, typo } from '@entities/fleet';
import {
  MONTHS_SHORT,
  formatMoney0,
  type MaterialKind,
  type SummaryCell,
} from '@entities/mto/receipts';
import { EmptyCard, KeyButton, Matrix, TableBox, Toolbar, Totals } from './styles';

type View = 'bdr' | 'raw';

interface Line {
  key: string;
  label: string;
  sub?: string;
  objectId: string | null;
  months: number[];
  total: number;
}

interface Group {
  key: string;
  label: string;
  note?: string;
  lines: Line[];
  months: number[];
  total: number;
}

const KIND_LINE: Readonly<Record<MaterialKind, string>> = {
  project: 'Согласно проектной документации',
  other: 'Прочие материалы',
};

function zero(): number[] {
  return Array.from({ length: 12 }, () => 0);
}

function add(
  line: { months: number[]; total: number },
  c: SummaryCell,
  field: 'amount' | 'amount_with_vat',
) {
  const v = Number(c[field]) || 0;
  const i = c.month - 1;
  if (i >= 0 && i < 12) line.months[i] = (line.months[i] ?? 0) + v;
  line.total += v;
}

/** «Как в БДР»: объект ОСНОВЫ или центр затрат → строки «по проекту» и «прочие». */
function bdrGroups(cells: readonly SummaryCell[], field: 'amount' | 'amount_with_vat'): Group[] {
  const groups = new Map<string, Group>();
  for (const c of cells) {
    const mapped = c.project_id !== null || c.econ_object_id !== null;
    const key = mapped
      ? `t:${c.project_id ?? c.econ_object_id}`
      : c.is_archive
        ? 'archive'
        : 'unmapped';
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        label: mapped
          ? (c.target_name ?? '—')
          : c.is_archive
            ? 'Архив (старые объекты)'
            : 'Не сопоставлено',
        note: mapped
          ? undefined
          : c.is_archive
            ? 'В БДР не идёт'
            : 'Сопоставьте на вкладке «Объекты прихода»',
        lines: [],
        months: zero(),
        total: 0,
      };
      groups.set(key, g);
    }
    const kind: MaterialKind = c.material_kind ?? 'other';
    const lineKey = mapped ? kind : `${kind}:${c.object_name ?? ''}`;
    let line = g.lines.find((l) => l.key === lineKey);
    if (!line) {
      line = {
        key: lineKey,
        label: mapped ? KIND_LINE[kind] : (c.object_name ?? 'Без объекта'),
        objectId: mapped ? null : c.object_id,
        months: zero(),
        total: 0,
      };
      g.lines.push(line);
    }
    add(line, c, field);
    add(g, c, field);
  }
  const order = (g: Group) => (g.key === 'unmapped' ? 1 : g.key === 'archive' ? 2 : 0);
  return [...groups.values()]
    .map((g) => ({
      ...g,
      // У объекта — сначала «по проекту», потом «прочие», как в БДР.
      lines: g.lines.sort((a, b) =>
        g.key.startsWith('t:') ? a.key.localeCompare(b.key) * -1 : b.total - a.total,
      ),
    }))
    .sort((a, b) => order(a) - order(b) || b.total - a.total);
}

/** «Как записано»: написание объекта в книге → куда сопоставлено. */
function rawLines(cells: readonly SummaryCell[], field: 'amount' | 'amount_with_vat'): Line[] {
  const lines = new Map<string, Line>();
  for (const c of cells) {
    const key = c.object_id ?? `name:${c.object_name ?? ''}`;
    let l = lines.get(key);
    if (!l) {
      l = {
        key,
        label: c.object_name ?? 'Без объекта',
        sub: c.target_name
          ? `→ ${c.target_name}${c.mapped_by_rule ? ' (предложено)' : ''}`
          : c.is_archive
            ? 'архив'
            : 'не сопоставлен',
        objectId: c.object_id,
        months: zero(),
        total: 0,
      };
      lines.set(key, l);
    }
    add(l, c, field);
  }
  return [...lines.values()].sort((a, b) => b.total - a.total);
}

/**
 * Свод прихода за год по месяцам: «Как в БДР» — по объектам ОСНОВЫ и
 * центрам затрат со строками «по проекту» и «прочие» (то, что ляжет в БДР),
 * «Как записано» — по написаниям объекта в книге, как её общий свод.
 * Суммы без копеек; без НДС — как в БДР, с НДС — для сверки с книгой.
 */
export function ReceiptsSummary({
  year,
  years,
  onYear,
  onOpenObject,
}: {
  year: number;
  years: readonly number[];
  onYear: (year: number) => void;
  onOpenObject: (objectId: string, month?: number) => void;
}) {
  const [view, setView] = useState<View>('bdr');
  const [withVat, setWithVat] = useState(false);
  const { data, isLoading, error, isFetching, refetch } = useGetMtoReceiptSummaryQuery({ year });
  const field = withVat ? 'amount_with_vat' : 'amount';
  const cells = useMemo(() => data?.cells ?? [], [data]);
  const groups = useMemo(() => bdrGroups(cells, field), [cells, field]);
  const raw = useMemo(() => rawLines(cells, field), [cells, field]);
  const totals = useMemo(() => {
    const t = { months: zero(), total: 0 };
    for (const c of cells) add(t, c, field);
    return t;
  }, [cells, field]);
  const yearOptions = years.includes(year) ? years : [year, ...years];
  const unmapped = groups.find((g) => g.key === 'unmapped');

  function cellButton(line: Line, i: number) {
    const v = line.months[i] ?? 0;
    if (v === 0) return <span aria-hidden="true">·</span>;
    if (!line.objectId) return formatMoney0(v);
    const objectId = line.objectId;
    return (
      <KeyButton
        type="button"
        title={`Строки прихода: ${line.label}, ${MONTHS_SHORT[i]}`}
        onClick={() => onOpenObject(objectId, i + 1)}
      >
        {formatMoney0(v)}
      </KeyButton>
    );
  }

  return (
    <>
      <Toolbar>
        <Select
          aria-label="Год"
          value={String(year)}
          onChange={(e) => onYear(Number(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        <Segmented role="group" aria-label="Как показать свод">
          <SegmentButton type="button" aria-pressed={view === 'bdr'} onClick={() => setView('bdr')}>
            Как в БДР
          </SegmentButton>
          <SegmentButton type="button" aria-pressed={view === 'raw'} onClick={() => setView('raw')}>
            Как записано
          </SegmentButton>
        </Segmented>
        <Segmented role="group" aria-label="Суммы">
          <SegmentButton type="button" aria-pressed={!withVat} onClick={() => setWithVat(false)}>
            Без НДС
          </SegmentButton>
          <SegmentButton type="button" aria-pressed={withVat} onClick={() => setWithVat(true)}>
            С НДС
          </SegmentButton>
        </Segmented>
      </Toolbar>

      {data && cells.length > 0 && (
        <Totals>
          <span>
            За {year} год: <b>{formatMoney0(totals.total)}</b> ₽ {withVat ? 'с НДС' : 'без НДС'}
          </span>
          {unmapped && (
            <span>
              Не сопоставлено: <b>{formatMoney0(unmapped.total)}</b> ₽
            </span>
          )}
        </Totals>
      )}

      {isLoading ? (
        <div aria-busy="true" aria-label="Загрузка свода">
          <SkeletonLine $height={40} />
          <SkeletonLine $height={20} />
          <SkeletonLine $height={20} $width="70%" />
        </div>
      ) : error !== undefined && !data ? (
        <Notice $tone="danger" role="alert">
          <span className="text">
            {apiLoadErrorText(error, 'Свод не загрузился.', 'Нет доступа к приходу ТМЦ.')}
          </span>
          <TextButton type="button" disabled={isFetching} onClick={() => void refetch()}>
            Повторить
          </TextButton>
        </Notice>
      ) : cells.length === 0 ? (
        <EmptyCard>
          <h3>За {year} год прихода нет</h3>
          <p>
            {typo('Свод строится сам из журнала прихода — загрузите книгу или внесите строки.')}
          </p>
        </EmptyCard>
      ) : (
        <TableBox>
          <Matrix>
            <thead>
              <tr>
                <th scope="col">{view === 'bdr' ? 'Объект и строка БДР' : 'Объект в книге'}</th>
                {MONTHS_SHORT.map((m) => (
                  <th key={m} scope="col" className="num">
                    {m}
                  </th>
                ))}
                <th scope="col" className="num">
                  Итого
                </th>
              </tr>
            </thead>
            <tbody>
              {view === 'bdr'
                ? groups.map((g) => (
                    <Fragment key={g.key}>
                      <tr className="group">
                        <td>
                          {g.label}
                          {g.note && <div className="sub">{g.note}</div>}
                        </td>
                        {g.months.map((v, i) => (
                          <td key={i} className="num">
                            {v === 0 ? '' : formatMoney0(v)}
                          </td>
                        ))}
                        <td className="num">{formatMoney0(g.total)}</td>
                      </tr>
                      {g.lines.map((l) => (
                        <tr key={l.key}>
                          <td>{l.label}</td>
                          {l.months.map((_, i) => (
                            <td key={i} className={(l.months[i] ?? 0) === 0 ? 'num empty' : 'num'}>
                              {cellButton(l, i)}
                            </td>
                          ))}
                          <td className="num">{formatMoney0(l.total)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))
                : raw.map((l) => (
                    <tr key={l.key}>
                      <td>
                        {l.label}
                        {l.sub && <div className="sub">{l.sub}</div>}
                      </td>
                      {l.months.map((_, i) => (
                        <td key={i} className={(l.months[i] ?? 0) === 0 ? 'num empty' : 'num'}>
                          {cellButton(l, i)}
                        </td>
                      ))}
                      <td className="num">{formatMoney0(l.total)}</td>
                    </tr>
                  ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Итого</td>
                {totals.months.map((v, i) => (
                  <td key={i} className="num">
                    {v === 0 ? '' : formatMoney0(v)}
                  </td>
                ))}
                <td className="num">{formatMoney0(totals.total)}</td>
              </tr>
            </tfoot>
          </Matrix>
        </TableBox>
      )}
    </>
  );
}
